import React, { useEffect, useState } from 'react';
import { Layout, Tag, Dropdown, Badge, FloatButton } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { isAdmin, getRole } from '../utils/auth';
import { palette, ROLE_COLORS } from '../theme';
import Pill from './Pill';

const { Header, Content } = Layout;

// Шестиугольная "гранёная" метка бренда — две треугольные грани разного
// цвета через clip-path (вместо прежнего трюка с border-треугольниками).
const BrandMark: React.FC<{ size?: number; leftColor: string; rightColor: string }> = ({
  size = 30, leftColor, rightColor,
}) => (
  <div style={{
    width: size, height: size, flexShrink: 0, display: 'flex',
    clipPath: 'polygon(50% 0%, 100% 15%, 100% 58%, 50% 100%, 0% 58%, 0% 15%)',
  }}>
    <div style={{ flex: 1, background: leftColor }} />
    <div style={{ flex: 1, background: rightColor }} />
  </div>
);

const NAV_DEFS = [
  { key: '/', label: 'Дашборд', dotColor: palette.navy },
  { key: '/entities', label: 'Юридические лица', dotColor: palette.blue },
  { key: '/upload', label: 'Загрузка файлов', dotColor: palette.orange },
  { key: '/tasks', label: 'Задачи на проверку', dotColor: palette.red },
  { key: '/rejected', label: 'Отбракованные записи', dotColor: palette.muted },
  { key: '/users', label: 'Пользователи', dotColor: palette.sky, adminOnly: true },
];

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [banners, setBanners] = useState<any[]>([]);
  const [openTasks, setOpenTasks] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const username = localStorage.getItem('username') || 'Пользователь';
  const role = getRole();
  const roleInfo = ROLE_COLORS[role] || ROLE_COLORS.viewer;
  const initials = username.slice(0, 2).toUpperCase();

  useEffect(() => {
    api.get('/banners/active').then(r => setBanners(r.data)).catch(() => {});
  }, []);

  // Реальный случай: счётчик открытых задач в сайдбаре грузился ОДИН РАЗ
  // при первом монтировании AppLayout (пустой массив зависимостей) — после
  // того как пользователь решал задачу на /tasks или из карточки ЮЛ на
  // /entities, бейдж продолжал показывать старое число вплоть до полной
  // перезагрузки страницы (F5), хотя сами задачи из списков пропадали
  // сразу. Перезапрашиваем при каждой смене маршрута — тот же сигнал, на
  // который уже реагирует подсветка активного пункта меню ниже, и он
  // естественно срабатывает именно тогда, когда пользователь уходит со
  // страницы, где только что решил задачу.
  useEffect(() => {
    api.get('/tasks/', { params: { status: 'open' } })
      .then(r => setOpenTasks(r.data.length))
      .catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const navItems = NAV_DEFS.filter(n => !n.adminOnly || isAdmin()).map(n => {
    const active = location.pathname === n.key;
    return { ...n, active, dotColor: active ? palette.blue : n.dotColor };
  });

  const bannerColor = (type: string) =>
    type === 'warning' ? palette.orange : type === 'error' ? palette.red : palette.blue;

  return (
    // Обычный <Layout> из antd определяет flex-direction:row только когда
    // среди детей есть настоящий <Sider> (регистрируется через контекст) —
    // без него он молча падает на flex-direction:column по умолчанию, и
    // самодельная панель разделов оказывается НАД контентом, а не слева от
    // него. Свой div с явным display:flex/flexDirection:row вместо
    // antd-магии — гарантированно горизонтальная раскладка независимо от
    // внутренней детекции Sider.
    <div style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
      <div style={{
        width: collapsed ? 72 : 236, flexShrink: 0, background: palette.navy,
        display: 'flex', flexDirection: 'column', transition: 'width 0.15s',
      }}>
        <div style={{
          padding: collapsed ? '22px 20px' : '22px 20px', display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 10, minHeight: 30,
        }}>
          <BrandMark leftColor={palette.sky} rightColor={palette.blue} />
          {!collapsed && (
            <span style={{
              color: '#fff', fontFamily: "'Lora', Georgia, serif", fontWeight: 600,
              fontSize: 14, letterSpacing: '0.01em', lineHeight: 1.25,
            }}>
              Санкционный<br />скрининг
            </span>
          )}
        </div>
        <div style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {navItems.map(item => (
            <div
              key={item.key}
              onClick={() => navigate(item.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px',
                borderRadius: 8, cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
                color: item.active ? '#fff' : 'rgba(255,255,255,0.72)',
                background: item.active ? 'rgba(255,255,255,0.06)' : 'transparent',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => {
                if (!item.active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.72)'; }
              }}
            >
              <div style={{ width: 7, height: 7, borderRadius: 2, flexShrink: 0, background: item.dotColor }} />
              {!collapsed && <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>{item.label}</span>}
              {item.key === '/tasks' && openTasks > 0 && (
                <Badge count={openTasks} size="small" style={{ marginLeft: collapsed ? 0 : 6 }} />
              )}
            </div>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <div
          onClick={() => setCollapsed(c => !c)}
          style={{
            margin: 12, padding: 10, textAlign: 'center', borderRadius: 8,
            background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.55)',
            fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
        >
          {collapsed ? '»' : '« СВЕРНУТЬ'}
        </div>
      </div>
      <Layout style={{ minWidth: 0 }}>
        <Header style={{
          background: palette.surface, padding: '0 28px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', height: 64,
          borderBottom: `1px solid ${palette.border}`,
        }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: palette.ink }}>
            Платформа санкционного скрининга
          </span>
          <Dropdown menu={{
            items: [
              {
                key: 'profile', disabled: true,
                label: <Pill bg={roleInfo.bg} fg={roleInfo.fg}>{roleInfo.label}</Pill>,
              },
              { type: 'divider' },
              { key: 'logout', label: 'Выйти', icon: <LogoutOutlined />, onClick: handleLogout },
            ]
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px 6px 6px',
              borderRadius: 20, cursor: 'pointer',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: palette.navy, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, flexShrink: 0,
              }}>
                {initials}
              </div>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: palette.ink }}>{username}</span>
              <Pill bg={roleInfo.bg} fg={roleInfo.fg}>{roleInfo.label}</Pill>
            </div>
          </Dropdown>
        </Header>
        {banners.map((b: any) => (
          <div key={b.id} style={{
            padding: '8px 24px', background: palette.cream,
            borderBottom: `1px solid ${palette.border}`,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Tag color={bannerColor(b.banner_type)} style={{ marginRight: 0 }}>{b.title}</Tag>
            <span style={{ fontSize: 13, color: palette.ink }}>{b.content}</span>
          </div>
        ))}
        <Content style={{
          margin: 16, padding: 24, background: palette.surface,
          borderRadius: 14, minHeight: 360, border: `1px solid ${palette.border}`,
        }}>
          <Outlet />
        </Content>
      </Layout>
      {/* Длинные таблицы (ЮЛ, задачи на проверку, отбракованные записи)
          требуют скроллить страницу целиком до самого низа и обратно —
          плавающая кнопка "наверх" появляется при прокрутке и мгновенно
          возвращает в начало страницы одним кликом. */}
      <FloatButton.BackTop visibilityHeight={200} />
    </div>
  );
};

export default AppLayout;
